import * as React from 'react';
import { useRef, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ActionStatus } from 'thunkless';
import * as _ from 'lodash';

import type { FormSubmitEventTarget } from '@/types/dom';
import { ticketContactIdSelector } from '@/redux/app/selectors/ticket';
import { contactAddTagStatusSelector } from '@/redux/app/selectors/contact';
import { Submitter } from '../shared/Submitter';

export interface TagFormProps {
  ticket: string;
  handleSubmit: (value: string) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
}

export const TagForm = ({ ticket, handleSubmit, inputRef }: TagFormProps) => {
  const submitterRef = useRef<Submitter>();
  const handleFormSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>&{ target: FormSubmitEventTarget }) => {
      event.preventDefault();
      const input = event.target.elements.namedItem('tag[name]');
      const value = input.value.trim();
      input.value = '';
      if (!value) return;
      handleSubmit(value);
      submitterRef.current.setStatus('submitting');
    },
    [handleSubmit, submitterRef],
  );
  const handlePress = useCallback((event) => {
    const input = event.target.form.elements.namedItem('tag[name]');
    const value = input.value.trim();
    input.value = '';
    if (!value) return;
    handleSubmit(value);
    submitterRef.current.setStatus('submitting');
  }, [handleSubmit, submitterRef]);

  const contactId = useSelector(_.partial(ticketContactIdSelector, ticket));
  const status = useSelector(_.partial(contactAddTagStatusSelector, contactId));
  useEffect(() => {
    if (status === ActionStatus.SUCCESS) submitterRef.current.setStatus('done');
    else if (status === ActionStatus.FAILURE) submitterRef.current.setStatus('error');
  }, [status, submitterRef]);

  const content = useMemo(() => (
    <div id="contact-tags" className="tag-form-container">
      <form
        className="hide tag-form"
        id="add-tag-form"
        acceptCharset="UTF-8"
        style={{ display: 'block' }}
        onSubmit={handleFormSubmit}
      >
        <div className="tag-input">
          <div className="ui-input-text ui-body-inherit ui-corner-all ui-shadow-inset ui-focus">
            <input
              ref={inputRef}
              placeholder="Add a Tag"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              type="text"
              name="tag[name]"
              id="tag_name"
              data-role="none"
            />
          </div>
          <Submitter ref={submitterRef} label="Add" id="add-tag-btn" handlePress={handlePress} />
        </div>

        <div className="tags typeahead" />
        <div className="tags-typeahead-results" />
      </form>
    </div>
  ), [ticket, handleFormSubmit, submitterRef, handlePress]);

  useEffect(() => { window.setupTagAutocomplete(); }, [content]);

  return content;
};
